// The Advanced drawer's Per-repo section writes (#5211, epic #5200 AC3) — a
// repo-scope route pin PUTs `/v1/llm-routes/repo/:repoId/:purpose`.
//
// ROUND 3 (#5381): reuses `agent-model-save.ts`'s WRITE machinery instead of a
// second copy of it (one-impl) — `planWrite` (never-wipe-a-field), `expectFor`
// (the #5208 precondition), `writeRoute` (generalized over `RouteTarget`, org
// or repo), `savedMessage`, and `Tenant`/`tenantNow`/`sameTenant` (the
// account-switch refusal). `refusal` answers a `PendingRefusal` — the SAME
// shape `ModelSaveNotice` already renders — so a repo-pin refusal gets the
// SAME cures the org picker's does ("Apply mine anyway", "Apply and reset
// thinking depth"), through `applyAnyway`/`applyWithoutEffort`.
//
// What this module does NOT reuse: `agent-model-save.ts`'s query-cache
// optimistic paint (`paint`/`settleRead`/pending-row `UNREAD` marker). A
// per-repo row is a rarer edit than the card's own picker, and every `choose`
// (a fresh, non-replayed write) is given `current` straight from the SAME
// `useLlmRoutes` cache this module's `refetch` refetches, which
// `AgentRepoOverridesSection` re-renders off — so `choose`'s `current` is
// always a real server-read row, never a synthetic in-flight paint. That is
// the one hazard the shared cache machinery exists to guard against for a
// FIRST write, so skipping it there trades nothing away: re-read, no
// optimistic paint, same as `agent-mode-save.ts`'s design decision for the
// drawer's mode writes. A REPLAY is a different hazard — see ROUND 5 below.
//
// ROUND 4 (#5381): the lock, the per-(repo,purpose) refusal store and the
// org-switch cleanup effect are `guarded-save.ts`'s (one-impl) — this file no
// longer hand-rolls them.
//
// ROUND 5 (#5381): a caller's `current` is stale exactly where it matters
// most — a replay. `applyAnyway`/`applyWithoutEffort` answer a 409/422 the
// section is STILL rendering the refused row for (the notice, not the row,
// is what the re-read after the 409 replaces); a click on "Apply mine
// anyway" between that refusal and the section's next re-render hands this
// module the SAME stale row the refusal just proved wrong. Building the
// forced PUT over it silently wipes whatever the OTHER writer's row carries
// that this row doesn't — the exact wipe the #5208 precondition exists to
// prevent, now reopened by the cure for its own refusal. So a replay no
// longer takes a `current` argument at all: it fetches the row itself
// (`fetchLlmRoutes`, `llmRoutes.ts`), under the write's own lock, right
// before planning the write — mirroring `agent-model-save.ts`'s `replay`
// (`reread: true`), except a raw GET rather than `client.refetchQueries`:
// this module keeps no query cache of its own, and `refetch()`'s default
// `cancelRefetch: true` can hand back a cancelled read's stale rows
// (`isError: false`) instead of rejecting — see `fetchLlmRoutes`'s doc.

import type { LlmRoutePurposeAny, NextRouteRow, RouteChange } from '@developerz/domain';
import { t } from '../i18n';
import {
  expectFor,
  type PendingRefusal,
  planWrite,
  rowOf,
  type SaveIntent,
  savedMessage,
  tenantNow,
  writeRoute,
} from './agent-model-save';
import { createGuardedSaves, type GuardedSaveCtx } from './guarded-save';
import { fetchLlmRoutes, type RouteExpect, type RouteWire } from './llmRoutes';
import { routeRefusalOf } from './route-refusal';
import type { ToastAction, ToastTone } from './toast';

const keyOf = (repoId: string, purpose: string): string => `${repoId}:${purpose}`;

/** How a replay treats the precondition and the effort — `agent-model-save.ts`'s own. */
type Mode = 'guarded' | 'force' | 'no_effort';

/**
 * The row that now exists at (repoId, purpose), right after a successful
 * write — `undefined` after a DELETE. Undo's own `send` call needs this as
 * ITS `before`: `planWrite`'s restore branch only emits a DELETE when its
 * `current` argument is non-null (there is something to delete), so an Undo
 * of a fresh pin (nothing existed before the write this is undoing) would
 * silently no-op without it — there is no shared cache here to read the
 * post-write row back from (see file header), so it is built from what the
 * write just sent plus its own echoed version.
 */
function writtenRow(
  repoId: string,
  purpose: string,
  next: NextRouteRow,
  saved: RouteExpect,
): RouteWire | undefined {
  if (next.op === 'delete') return undefined;
  return {
    id: `repo:${repoId}:${purpose}`,
    scope: 'repo',
    scope_ref: repoId,
    purpose,
    key_label: next.body.key_label ?? null,
    provider: next.body.provider ?? null,
    model: next.body.model ?? null,
    reasoning_effort: next.body.reasoning_effort,
    updated_at_iso: 'updated_at_iso' in saved ? saved.updated_at_iso : '',
  };
}

export type RepoRouteSaves = {
  readonly isBusy: (repoId: string, purpose: string) => boolean;
  /** The (repoId, purpose)'s standing refusal, or null. */
  readonly refusal: (repoId: string, purpose: string) => PendingRefusal | null;
  readonly dismiss: (repoId: string, purpose: string) => void;
  /** `current`: the repo's row for this purpose, as read — undefined = no pin yet. */
  readonly choose: (
    repoId: string,
    purpose: LlmRoutePurposeAny,
    current: RouteWire | undefined,
    change: RouteChange,
  ) => Promise<void>;
  /** After a 409: replay the refused intent over a FRESH read of the row —
   * never a caller-supplied `current`, which may still be the stale row the
   * refusal just proved wrong (ROUND 5, file header) — unconditionally. */
  readonly applyAnyway: (repoId: string, purpose: LlmRoutePurposeAny) => Promise<void>;
  /** After a reasoning refusal: replay it, over a fresh read, with the
   * thinking depth cleared. */
  readonly applyWithoutEffort: (repoId: string, purpose: LlmRoutePurposeAny) => Promise<void>;
};

export function createRepoRouteSaves(input: {
  readonly token: string | null;
  /** Re-read after every write, win or lose — no optimistic paint (see file header). */
  readonly refetch: () => Promise<unknown>;
  /** The page's ONE toast — the SAME `notify` `agent-model-save.ts` shares, so
   * an org switch's "drop the standing toast" effect (now `guarded-save.ts`'s,
   * one-impl) takes THIS controller's Undo down too (`Agents.tsx` wires all
   * three save controllers through the one `notify`/`dismiss`). */
  readonly notify: (message: string, tone?: ToastTone, action?: ToastAction) => void;
  /** Take the page's toast down — an org switch strands its Undo/refusal in
   * the other org (`guarded-save.ts`). */
  readonly dismissNotice: () => void;
}): RepoRouteSaves {
  const guarded = createGuardedSaves<SaveIntent>({
    fallback: t('agents.save.failed'),
    dismissNotice: input.dismissNotice,
    reread: () => input.refetch(),
  });

  /** The row for (repoId, purpose) in a freshly fetched list — never the
   * caller's cache. */
  const repoRouteOf = (
    rows: readonly RouteWire[],
    repoId: string,
    purpose: string,
  ): RouteWire | undefined =>
    rows.find((r) => r.scope === 'repo' && r.scope_ref === repoId && r.purpose === purpose);

  /** The write core: builds `next` over `before`, sends it, clears/holds the
   * refusal, and raises the Undo. Shared by `send` (a caller-given `before`)
   * and `replay` (a `before` fetched fresh, under the SAME lock — ROUND 5,
   * file header). */
  const attempt =
    (
      repoId: string,
      purpose: LlmRoutePurposeAny,
      intent: SaveIntent,
      before: RouteWire | undefined,
      mode: Mode,
    ) =>
    async (ctx: GuardedSaveCtx): Promise<void> => {
      const next = planWrite(before === undefined ? null : rowOf(before), intent, mode);
      ctx.clearRefusal();
      if (next === null) return;
      let saved: Awaited<ReturnType<typeof writeRoute>>;
      try {
        saved = await writeRoute(
          { scope: 'repo', repoId },
          purpose,
          next,
          expectFor(before, intent, mode),
          input.token,
        );
      } catch (err) {
        ctx.refuse(routeRefusalOf(err));
        return;
      }
      if (ctx.moved()) return;
      const undo: ToastAction | undefined =
        intent.kind === 'change'
          ? {
              label: t('agents.save.undo'),
              run: () =>
                void send(
                  repoId,
                  purpose,
                  {
                    kind: 'restore',
                    row: before === undefined ? null : rowOf(before),
                    expect: saved,
                    tenant: intent.tenant,
                  },
                  writtenRow(repoId, purpose, next, saved),
                  'guarded',
                ),
            }
          : undefined;
      input.notify(savedMessage(intent, next), 'success', undo);
    };

  const send = (
    repoId: string,
    purpose: LlmRoutePurposeAny,
    intent: SaveIntent,
    before: RouteWire | undefined,
    mode: Mode,
  ): Promise<void> =>
    guarded.run(keyOf(repoId, purpose), intent, attempt(repoId, purpose, intent, before, mode));

  /**
   * After a 409/422: re-fetch the row fresh (`fetchLlmRoutes`, never
   * `refetch()` — see file header) under the write's own lock, THEN attempt
   * the replay over it. A fetch that throws refuses `failed`, unsent — never
   * a forced write over a guess. The tenant is checked again right after the
   * fetch's only await, same as every other write here (`guarded.run`'s own
   * top-level check only catches a switch BEFORE this attempt started).
   */
  const replay = (repoId: string, purpose: LlmRoutePurposeAny, mode: Mode): Promise<void> => {
    const k = keyOf(repoId, purpose);
    const pending = guarded.refusal(k);
    if (pending === null) return Promise.resolve();
    return guarded.run(k, pending.intent, async (ctx) => {
      let rows: readonly RouteWire[];
      try {
        rows = await fetchLlmRoutes(input.token);
      } catch {
        ctx.refuse({ kind: 'failed', detail: null });
        return;
      }
      if (ctx.refuseMoved()) return;
      const before = repoRouteOf(rows, repoId, purpose);
      await attempt(repoId, purpose, pending.intent, before, mode)(ctx);
    });
  };

  return {
    isBusy: (repoId, purpose) => guarded.isBusy(keyOf(repoId, purpose)),
    refusal: (repoId, purpose) => guarded.refusal(keyOf(repoId, purpose)),
    dismiss: (repoId, purpose) => guarded.dismiss(keyOf(repoId, purpose)),
    choose: (repoId, purpose, current, change) =>
      send(repoId, purpose, { kind: 'change', change, tenant: tenantNow() }, current, 'guarded'),
    applyAnyway: (repoId, purpose) => replay(repoId, purpose, 'force'),
    applyWithoutEffort: (repoId, purpose) => replay(repoId, purpose, 'no_effort'),
  };
}
